import React from 'react';
import config from 'config';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import intersection from 'lodash/intersection';
import { SECOND } from 'shared/utils/timeConstants';
import preventDefault from 'shared/utils/preventDefault';
import requestsStatuses from 'modules/common/constants/requestsStatuses';

import {
  Input,
  Button,
  CircleTimer,
} from 'shared/components';
import {
  EMPTY_OTP_CODE_LOCAL,
  INVALID_OTP_CODE_LOCAL,
} from '../../constants/localErrorCodes';

import {
  EMPTY_OTP_CODE,
  INVALID_OTP_CODE,
  WRONG_OTP_CODE,
  EXPIRED_OTP_CODE,
  LOGIN_FREEZED_DUE_TO_MANY_ATTEMPTS,
} from '../../constants/apiErrorCodes';
import theme from './OtpForm.scss';

const propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  status: PropTypes.string.isRequired,
  errorCodes: PropTypes.arrayOf(PropTypes.number).isRequired,
  isSendOtpAgainEnabled: PropTypes.bool.isRequired,
  otpCodeValue: PropTypes.string.isRequired,
  formattedPhoneNumber: PropTypes.string.isRequired,

  handleOtpInputChange: PropTypes.func.isRequired,
  handleOtpInputFocus: PropTypes.func.isRequired,
  handleSendOtpTimerFinish: PropTypes.func.isRequired,
  handleSendOtpAgainButtonClick: PropTypes.func.isRequired,
  handleFormSubmit: PropTypes.func.isRequired,
};

export const otpCodeInputErrorCodes = [
  EMPTY_OTP_CODE_LOCAL,
  INVALID_OTP_CODE_LOCAL,
  EMPTY_OTP_CODE,
  INVALID_OTP_CODE,
  WRONG_OTP_CODE,
  EXPIRED_OTP_CODE,
  LOGIN_FREEZED_DUE_TO_MANY_ATTEMPTS,
];

const errorMessagesMap = {
  [EMPTY_OTP_CODE_LOCAL]: {
    id: 'PWA.signIn.OTPField.EmptyFieldErrorMessge',
    defaultMessage: '** Enter the temporary code sent to you',
  },
  [INVALID_OTP_CODE_LOCAL]: {
    id: 'Authentication.Otp.Patient.Error.IncorrectOTP',
    defaultMessage: '** The code you entered is incorrect. Please recheck and try again',
  },
  [INVALID_OTP_CODE]: {
    id: 'Authentication.Otp.Patient.Error.IncorrectOTP',
    defaultMessage: '** The code you entered is incorrect. Please recheck and try again',
  },
  [WRONG_OTP_CODE]: {
    id: 'Authentication.Otp.Patient.Error.IncorrectOTP',
    defaultMessage: '** The code you entered is incorrect. Please recheck and try again',
  },
  [EXPIRED_OTP_CODE]: {
    id: 'Authentication.Otp.Error.OTPExpired',
    defaultMessage: '** The code you entered has expired and is no longer usable. Click \'Send code again\' to receive a new code',
  },
};

const OtpForm = ({
  intl,
  status,
  errorCodes,
  isSendOtpAgainEnabled,
  otpCodeValue,
  handleOtpInputChange,
  handleOtpInputFocus,
  handleSendOtpTimerFinish,
  handleSendOtpAgainButtonClick,
  handleFormSubmit,
  formattedPhoneNumber,
}) => {
  const otpErrorCodes = intersection(errorCodes, otpCodeInputErrorCodes);
  const optCodeTooltipErrorMessage = otpErrorCodes.length ? errorMessagesMap[otpErrorCodes[0]] : null;
  const otpCodeInputStatus = otpErrorCodes.length ? requestsStatuses.failure : status;
  const otpLabelTranslationId = config.product_name === 'clalit'
    ? 'Authentication.Otp.Label.OTPSent.Placeholder'
    : 'Authentication.Otp.Label.OTPSent';
  const msg = intl.formatMessage({
    id: otpLabelTranslationId,
    defaultMessage: '** We sent you a time code.<br />Please enter the code',
  }, { phonenumber: formattedPhoneNumber });

  return (
    <div className={ theme.root }>
      <h2 className={ theme.title }>
        <span>{ msg }</span>
      </h2>

      <form
        className={ theme.form }
        onSubmit={ preventDefault(handleFormSubmit) }
      >
        <Input
          testId="verify-otp-field"
          defaultValue={ otpCodeValue }
          ariaLabel={{ id: 'Authentication.Otp.Patient.Label.OTPPlaceholder', defaultMessage: '** OTP' }}
          placeholder={{ id: 'Authentication.Otp.Patient.Label.OTPPlaceholder', defaultMessage: '** OTP' }}
          iconName={ config.product_name !== 'clalit' ? 'phone-sms' : undefined }
          iconPositionLeft={ true }
          iconPositionRtlNotReversed={ true }
          status={ otpCodeInputStatus }
          greenSuccessStatus={ true }
          size="large"
          mediaResized={ true }
          validatePattern="^[0-9\b]+$"
          disabled={ status === requestsStatuses.pending || status === requestsStatuses.success }
          onChange={ handleOtpInputChange }
          onFocus={ handleOtpInputFocus }
          autoFocus={ true }
          maxLength={ 6 }
          tooltipTestId="otp-form-tooltip"
          highlightTextOnError={ config.product_name === 'clalit' }
          tooltipMessage={ optCodeTooltipErrorMessage }
          required
          rootClassName={ theme.input }
          inputMode="numeric"
        />

        <Button
          testId="verify-button"
          label={ intl.formatMessage({ id: 'Authentication.Otp.Label.VerifyMe', defaultMessage: '** Verify Me' }) }
          iconName="arrow-right"
          isLoading={ status === requestsStatuses.pending }
          size="large"
          disabled={ status === requestsStatuses.pending || status === requestsStatuses.success }
          type="submit"
          mediaResized
          fullWidth
        />
      </form>

      <div className={ theme.footerContainer }>
        { !isSendOtpAgainEnabled && (
          <CircleTimer
            ticksAmount={ 60 }
            intervalBetweenTicks={ SECOND }
            reverse={ true }
            onComplete={ handleSendOtpTimerFinish }
          />
        ) }

        <Button
          className={ classnames(theme.circleTimerButton, { [theme.disabled]: !isSendOtpAgainEnabled || status === requestsStatuses.pending || status === requestsStatuses.success }) }
          testId="send-sms-button"
          buttonType="naked"
          label={ intl.formatMessage({
            id: 'Authentication.Otp.Label.SendOTPAgain',
            defaultMessage: '** Send Code Again',
          }) }
          disabled={ !isSendOtpAgainEnabled || status === requestsStatuses.pending || status === requestsStatuses.success }
          onClick={ handleSendOtpAgainButtonClick }
          mediaResized
        />
      </div>
    </div>
  );
};

OtpForm.propTypes = propTypes;

export default OtpForm;
